import React from "react";
import GLTextField, { GLTextFieldProps } from "../GLTextField";
import { validateEmailString } from "../../../../../utils/utils";

/**
 * Canonical form for stored/submitted email: strip all whitespace.
 * Matches what we validate and what parents should persist.
 */
export function normalizeEmailInput(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s/g, "");
}

const GLEmailField: React.FC<GLTextFieldProps> = ({
  label,
  id,
  onChange,
  tooltip,
  value,
  defaultValue,
  required,
  disabled,
  placeholder,
  additionalValidation: extraValidation,
  maxLength = 200
}) => {
  const emailFormatMessage = (normalized: string): string | undefined => {
    if (normalized.length > 0 && !validateEmailString(normalized)) {
      return "Please enter a valid email address";
    }
    return undefined;
  };

  const mergedAdditionalValidation = (
    newValue = ""
  ): string | undefined => {
    // Same normalization as onChange so helper text / invalid state match stored values
    // (e.g. confirm-email match vs primary, which is always whitespace-stripped).
    const normalized = normalizeEmailInput(newValue);
    const formatErr = emailFormatMessage(normalized);
    if (formatErr) {
      return formatErr;
    }
    return extraValidation?.(normalized);
  };

  if (disabled || !onChange) {
    return (
      <GLTextField
        label={label}
        id={id}
        tooltip={tooltip}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onChange={onChange}
        additionalValidation={mergedAdditionalValidation}
        maxLength={maxLength}
      />
    );
  }

  const handleChange = (data: {
    e: React.ChangeEvent<HTMLInputElement>;
    isInvalid: boolean;
  }) => {
    const normalized = normalizeEmailInput(data.e.target.value);
    const synthetic = {
      ...data.e,
      target: {
        ...data.e.target,
        value: normalized
      }
    } as React.ChangeEvent<HTMLInputElement>;

    const formatErr = emailFormatMessage(normalized);
    const extraErr = extraValidation?.(normalized);
    const isInvalid =
      (!!required && !normalized) ||
      normalized.length > maxLength ||
      !!(formatErr ?? extraErr);

    onChange({ e: synthetic, isInvalid });
  };

  return (
    <GLTextField
      label={label}
      id={id}
      tooltip={tooltip}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onChange={handleChange}
      additionalValidation={mergedAdditionalValidation}
      maxLength={maxLength}
    />
  );
};

export default GLEmailField;
